import React, { useEffect, useState } from "react";
import { Character, CharacterRequest, CampaignListItem } from "../../types/types";
import { Button, Input, TextArea, Select, PaywallModal } from "../../components/ui";
import { useFormValidation } from "../../hooks";
import { commonValidationRules } from "../../utils/validation";
import { CampaignService } from "../../services/campaignService";
import { PaywallError } from "../../services/apiClient";

interface CharacterFormProps {
  character?: Character;
  onSave: (_data: CharacterRequest) => Promise<void>;
  onCancel: () => void;
}

const CharacterForm: React.FC<CharacterFormProps> = ({ character, onSave, onCancel }) => {
  const [availableCampaigns, setAvailableCampaigns] = useState<CampaignListItem[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [paywall, setPaywall] = useState<{ resource: string; limit: number } | null>(null);
  const form = useFormValidation<CharacterRequest>({
    initialValues: {
      name: "",
      description: "",
      imageUrl: "",
      notes: "",
      campaignId: null,
    },
    validationRules: {
      name: commonValidationRules.name,
      description: commonValidationRules.description,
      imageUrl: commonValidationRules.url,
    },
    onSubmit: async (values) => {
      try {
        await onSave(values);
      } catch (err) {
        if (err instanceof PaywallError) {
          setPaywall({ resource: err.resource, limit: err.limit });
          return;
        }
        console.error("Failed to save character:", err);
        throw err;
      }
    },
  });

  // Initialize form with character data when editing
  useEffect(() => {
    if (character) {
      form.setValues({
        name: character.name,
        description: character.description,
        imageUrl: character.imageUrl,
        notes: character.notes,
        campaignId: character.campaignId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character]); // Removed 'form' from dependencies to prevent infinite re-renders

  // Fetch available campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoadingCampaigns(true);
        const campaigns = await CampaignService.getAvailableCampaigns();
        setAvailableCampaigns(campaigns);
      } catch (error) {
        console.error("Failed to fetch campaigns:", error);
        setAvailableCampaigns([]);
      } finally {
        setLoadingCampaigns(false);
      }
    };

    fetchCampaigns();
  }, []);

  return (
    <div className="bg-zinc-950 text-white min-h-screen p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-zinc-900 border border-zinc-800 p-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-light text-white tracking-wide">
              {character ? "Edit Hero" : "Create New Hero"}
            </h1>
            <Button onClick={onCancel} variant="ghost" size="md">
              Cancel
            </Button>
          </div>

          <form onSubmit={form.handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-zinc-800 border border-zinc-700 p-6">
              <h2 className="text-sm font-semibold text-amber-400 tracking-wide mb-4">
                Basic Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    label="Character Name"
                    value={form.values.name}
                    onChange={form.handleChange("name")}
                    onBlur={form.handleBlur("name")}
                    error={form.errors.name}
                    fullWidth
                    placeholder="Enter character name"
                    variant="filled"
                  />
                </div>

                <div>
                  <Input
                    type="url"
                    id="imageUrl"
                    name="imageUrl"
                    label="Image URL"
                    value={form.values.imageUrl}
                    onChange={form.handleChange("imageUrl")}
                    onBlur={form.handleBlur("imageUrl")}
                    error={form.errors.imageUrl}
                    fullWidth
                    placeholder="https://example.com/character-image.jpg"
                    variant="filled"
                  />
                </div>
              </div>

              <div className="mt-6">
                <TextArea
                  id="description"
                  name="description"
                  label="Description"
                  value={form.values.description}
                  onChange={form.handleChange("description")}
                  onBlur={form.handleBlur("description")}
                  error={form.errors.description}
                  rows={4}
                  fullWidth
                  placeholder="Describe your character's appearance, personality, or concept..."
                  variant="filled"
                />
              </div>

              <div className="mt-6">
                <TextArea
                  id="notes"
                  name="notes"
                  label="Notes & Backstory"
                  value={form.values.notes}
                  onChange={form.handleChange("notes")}
                  onBlur={form.handleBlur("notes")}
                  error={form.errors.notes}
                  rows={6}
                  fullWidth
                  placeholder="Character backstory, motivations, relationships, or any other notes..."
                  variant="filled"
                />
              </div>

              <div className="mt-6">
                <Select
                  key={`campaign-select-${availableCampaigns.length}-${form.values.campaignId}`}
                  id="campaignId"
                  name="campaignId"
                  label="Chronicle (Optional)"
                  value={form.values.campaignId?.toString() || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    const campaignId = value === "" ? null : parseInt(value, 10);
                    form.setValue("campaignId", campaignId);
                  }}
                  onBlur={form.handleBlur("campaignId")}
                  error={form.errors.campaignId}
                  options={[
                    { value: "", label: "Select a chronicle (optional)" },
                    ...availableCampaigns.map((campaign) => ({
                      value: campaign.id.toString(),
                      label: campaign.name,
                    })),
                  ]}
                  helpText={`Associate this hero with a chronicle where you are owner or invited (${availableCampaigns.length} chronicles available)`}
                  fullWidth
                  variant="filled"
                  disabled={loadingCampaigns}
                />
                {loadingCampaigns && (
                  <div className="mt-2 text-sm text-zinc-400">Loading available chronicles...</div>
                )}
                {!loadingCampaigns && availableCampaigns.length === 0 && (
                  <div className="mt-2 text-sm text-amber-400">
                    No chronicles available. Create a chronicle first or get invited to one.
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6">
              <Button type="button" onClick={onCancel} variant="secondary" size="md">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={form.isSubmitting}
                variant="primary"
                size="md"
                loading={form.isSubmitting}
              >
                {character ? "Update Hero" : "Create Hero"}
              </Button>
            </div>
          </form>
        </div>
      </div>
      <PaywallModal
        open={!!paywall}
        resource={paywall?.resource ?? ""}
        limit={paywall?.limit ?? 0}
        onClose={() => setPaywall(null)}
      />
    </div>
  );
};

export default CharacterForm;
